import { getUserError } from '../../../components/miscUtils';
import { getColumnInfo, getDendrogram, getCanonicalTypeInfo } from '../../../data/analyticsDataSlice';
import { perfTestStart } from '../../../data/analyticsDataStorage';
import { TpDataContext, TpDataTable, TpDataTableColumn, TpCanonicalTypeInfo } from '../../../data/interfaces';
import { TpActiveEnSelectionFilter } from '../../../data/selection/interfaces';
import { getEnSelectionFilterDef } from '../../../data/selection/selectionEnFilters';
import { TpChartManifest } from './interfaces';

interface TpAspectDataBinding {
  columnId: string | null;
}

export interface TpChartDataBinding {
  dataTableId: string;
  columnsCanonicalTypeId?: string; // in case data is bound to a matrix, defines the item type of the columns to include
  columnsGroupId?: string; // in case data is bound to a matrix, defines from what columns group to take the columns of the matrix
  aspectBindings: { [aspectId: string]: TpAspectDataBinding };
  compoundAspectBindings?: { [aspectId: string]: string[] };
  filter?: TpActiveEnSelectionFilter; // optionally specifies a filter to be applied on the data used to render this chart
  secondDataTableId?: string; // in case a second table is
  secondAspectBindings?: { [aspectId: string]: TpAspectDataBinding }; // in case a second table is involved, define the bindings for that
}

export function getDataTable(dataContext: TpDataContext, dataTableId: string) {
  for (const iterDataTable of dataContext.dataTables) if (iterDataTable.id == dataTableId) return iterDataTable;

  throw getUserError(`Datatable not found: ${dataTableId}`);
}

export function getDataTableMeta(dataContext: TpDataContext, dataTableId: string) {
  for (const iterDataTable of dataContext.dataTables) if (iterDataTable.id == dataTableId) return iterDataTable;
  for (const iterDataTable of dataContext.lazyLoadingTableMetas)
    if (iterDataTable.id == dataTableId) return iterDataTable;

  throw getUserError(`Datatable not found: ${dataTableId}`);
}

export function getDataTableByCanonicalType(dataContext: TpDataContext, canonicalTypeId: string) {
  // Add a warning in case multiple datatables exist with the same canonical type?
  if (dataContext.dataTables.filter((dt) => dt.canonicalTypeId === canonicalTypeId).length > 1) {
    console.warn(`Multiple datatables found with canonical type: ${canonicalTypeId}. Returning the first one found.`);
  }

  for (const iterDataTable of dataContext.dataTables) {
    // IMPORTANT: here we return the first one found!
    if (iterDataTable.canonicalTypeId == canonicalTypeId) return iterDataTable;
  }
  throw getUserError(`No datatable found with canonical type: ${canonicalTypeId}`);
}

export function isDendrogramBinding(binding: string | null): boolean {
  if (!binding) return false;
  return binding.startsWith('_dendrogram_');
}

export function getDendrogramBinding(binding: string | null): string {
  if (!binding || !binding.startsWith('_dendrogram_')) throw getUserError('Binding is not to a dendrogram');
  return binding!.substring(12);
}

export interface TpAspectBinding {
  columnId: string;
  columnInfo: TpDataTableColumn;
  data: any[];
}

export interface TpBoundData {
  dataTable: TpDataTable;
  canonicalTypeInfo: TpCanonicalTypeInfo;
  restrictionFilter: (columnData: any[]) => any[]; //converts a "full" set of column data to the current restriction
  getItemIndexById: (itemId: string) => number; // converts an item id into the index nunber in the (restricted or full) set
  bindings: {
    [aspectId: string]: TpAspectBinding;
  };
  compoundBindings: {
    [aspectId: string]: TpAspectBinding[];
  };
  matrixColumnInfo?: {
    columnCanonicalTypeInfo: TpCanonicalTypeInfo;
    matrixColumns: TpDataTableColumn[]; // the set of columns in the bound datatable that is part of the matrix (i.e. that has the right type defined)
    dataTableColumns: TpDataTable; // the linked datatable that contains info on the matrix columns
    matrixColumnsData: { [colId: string]: any[] };
  };
}

export function getBoundData(
  chartTypeManifest: TpChartManifest,
  dataContext: TpDataContext,
  binding: TpChartDataBinding,
): TpBoundData {
  const pf = perfTestStart();
  const dataTable = getDataTable(dataContext, binding.dataTableId);
  const itemIdList = dataTable.columnData[dataTable.colItemId];
  const canonicalTypeInfo = getCanonicalTypeInfo(dataContext, dataTable.canonicalTypeId);

  let restrictionList = canonicalTypeInfo.restrictionList;
  let restrictedItemId2Inxdex = canonicalTypeInfo.restrictedItemId2Inxdex;

  if (binding.filter) {
    // we have a filter defined that applies to this specific chart
    let startItemList = itemIdList; // set of items to be used at the start of the filter
    if (canonicalTypeInfo.restrictedItemsMap)
      // we need to apply the restriction, if present
      startItemList = startItemList.filter((item: any) => canonicalTypeInfo.restrictedItemsMap![item]);
    let filteredMap: { [id: string]: boolean } = {};
    for (const item of startItemList) filteredMap[item] = true;
    for (const step of binding.filter.steps) {
      const nextStepFilteredMap: { [id: string]: boolean } = {};
      const stepSelectedItemIds = getEnSelectionFilterDef(step.type).performStep(dataContext, canonicalTypeInfo, step);
      for (const item of stepSelectedItemIds) if (filteredMap[item]) nextStepFilteredMap[item] = true;
      filteredMap = nextStepFilteredMap;
    }
    restrictionList = [];
    restrictedItemId2Inxdex = {};
    for (let idx = 0; idx < itemIdList.length; idx++)
      if (filteredMap[itemIdList[idx]]) {
        restrictedItemId2Inxdex[itemIdList[idx]] = restrictionList.length;
        restrictionList.push(idx);
      }
  }

  function restrictionFilter(columnData: any[]): any[] {
    if (!restrictionList) return columnData;
    return restrictionList.map((idx: number) => columnData[idx]);
  }

  function getItemIndexById(itemId: string): number {
    return restrictedItemId2Inxdex[itemId];
  }

  const rs: any = {
    dataTable,
    bindings: {
      itemId: {
        columnId: dataTable.colItemId,
        data: restrictionFilter(itemIdList),
        columnInfo: getColumnInfo(dataTable, dataTable.colItemId),
      },
    },
    compoundBindings: {},
    canonicalTypeInfo,
    restrictionFilter,
    getItemIndexById,
  };

  let dataTableColumns: TpDataTable;
  if (binding.columnsCanonicalTypeId)
    dataTableColumns = getDataTableByCanonicalType(dataContext, binding.columnsCanonicalTypeId);

  let matrixColumns: TpDataTableColumn[] = [];
  if (binding.columnsCanonicalTypeId) {
    // in case the binding defines a data matrix, we add additional info about the matrix, and the datable that relates to the columns
    const dataTableColumns = getDataTableByCanonicalType(dataContext, binding.columnsCanonicalTypeId);
    matrixColumns = dataTable.columns.filter(
      (column) =>
        column.columnCanonicalTypeId == binding.columnsCanonicalTypeId && column.groupId == binding.columnsGroupId,
    );
    const matrixColumnsData: { [colId: string]: any } = {};
    for (const mCol of matrixColumns) {
      matrixColumnsData[mCol.id] = restrictionFilter(dataTable.columnData[mCol.id]);
    }
    rs.matrixColumnInfo = {
      columnCanonicalTypeInfo: getCanonicalTypeInfo(dataContext, binding.columnsCanonicalTypeId),
      dataTableColumns,
      matrixColumns,
      matrixColumnsData,
    };
  }

  // in case of datamatrix: we collect for which aspects we should look into the linked datatable for the columns
  const aspectIsMappedToColumnDataTable: { [id: string]: any } = {};
  for (const aspect of chartTypeManifest.aspects)
    if (aspect.onColumnItems) aspectIsMappedToColumnDataTable[aspect.id] = true;

  for (const aspectId in binding.aspectBindings) {
    let columnInfo = null;
    let columnId = binding.aspectBindings[aspectId].columnId;
    let columnName = '-';
    let columnData = null;
    if (columnId) {
      if (!aspectIsMappedToColumnDataTable[aspectId]) {
        if (restrictionList && isDendrogramBinding(columnId)) columnId = dataTable.colItemId; // we can't have a dendrogram when the set is restricted...
        // aspect is bound to the actual datatable of the chart
        if (!isDendrogramBinding(columnId)) {
          // normal column
          columnData = restrictionFilter(dataTable.columnData[columnId]);
          columnInfo = getColumnInfo(dataTable, columnId);
          columnName = columnInfo.name;
          if (!columnData)
            throw getUserError(`Unable to bind aspect ${aspectId} to column ${columnId}: data not found`);
        } else {
          // coming from dendrogram (likely for sorting => we return sort index as a field)
          const dendrogram = getDendrogram(dataContext, getDendrogramBinding(columnId));
          columnData = itemIdList.map((itemId: string) => dendrogram.itemId2PosIndexMap[itemId]);
        }
      } else {
        // aspect is bound to the datatable linked to the columns
        if (!isDendrogramBinding(columnId)) {
          // normal column
          columnInfo = getColumnInfo(dataTableColumns!, columnId);
          columnName = columnInfo.name;
          const boundColumnMapper: any = {};
          for (const column of dataTable.columns)
            if (
              column.columnCanonicalTypeId == binding.columnsCanonicalTypeId &&
              column.groupId == binding.columnsGroupId
            ) {
              const linkedTableIdx = dataTableColumns!.itemIdMap[column.columnItemId!];
              boundColumnMapper[column.id] = dataTableColumns!.columnData[columnId][linkedTableIdx];
            }
          columnData = matrixColumns.map((matrixColumn) => boundColumnMapper[matrixColumn.id]);
        } else {
          // coming from dendrogram (likely for sorting => we return sort index as a field)
          const dendrogram = getDendrogram(dataContext, getDendrogramBinding(columnId));
          columnData = matrixColumns.map((matrixColumn) => dendrogram.itemId2PosIndexMap[matrixColumn.columnItemId!]);
        }
      }
    }
    rs.bindings[aspectId] = {
      columnId,
      columnInfo,
      data: columnData,
    };
  }

  if (binding.compoundAspectBindings) {
    for (const aspectId in binding.compoundAspectBindings) {
      const columns = [];
      for (const columnId of binding.compoundAspectBindings[aspectId]) {
        const colBinding: TpAspectBinding = {
          columnId,
          columnInfo: getColumnInfo(dataTable, columnId),
          data: restrictionFilter(dataTable.columnData[columnId]),
        };
        columns.push(colBinding);
      }
      rs.compoundBindings[aspectId] = columns;
    }
  }

  return rs;
}

export function getSecondBoundData(
  chartTypeManifest: TpChartManifest,
  dataContext: TpDataContext,
  binding: TpChartDataBinding,
): TpBoundData {
  // We apply a bit of a hack here, where we copy the definition of the second bindings to the primary one, and call the function for the primary
  const binding2: TpChartDataBinding = {
    dataTableId: binding.secondDataTableId!,
    aspectBindings: binding.secondAspectBindings!,
  };
  return getBoundData(chartTypeManifest, dataContext, binding2);
}

export interface TpChartConfigParams {
  userCanRemove?: boolean;
  [configParamId: string]: any;
}
